import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  IconButton,
  TextField,
  Button,
  Avatar,
  Chip,
  Tooltip,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";

/**
 * props:
 * - open: boolean
 * - onClose: () => void
 * - product: {
 *     id, name, sku, image,
 *     variations: [{ color, sizes: [{ size, stock, price, status, variantId? }] }]
 *   }
 * - onConfirm: ({ color, size, price, stock, quantity, variantId }) => void
 */
export default function VariantPickerDialog({ open, onClose, product, onConfirm }) {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const formatMoney = useCallback(
    (v) => `${Number(v || 0).toLocaleString("vi-VN")}₫`,
    []
  );

  // size available: stock > 0 and status ok (Y/true/undefined)
  const isSizeAvailable = useCallback((s) => {
    const okStatus =
      s?.status === undefined || s?.status === "Y" || s?.status === true;
    return (s?.stock ?? 0) > 0 && okStatus;
  }, []);

  // Swatch màu (nếu string là màu/hex phổ biến)
  const cssColor = useCallback((c) => {
    if (!c) return null;
    const common = {
      black: "#000",
      white: "#fff",
      red: "#f44336",
      blue: "#2196f3",
      green: "#4caf50",
      yellow: "#ffeb3b",
      orange: "#ff9800",
      purple: "#9c27b0",
      gray: "#9e9e9e",
      grey: "#9e9e9e",
      silver: "#c0c0c0",
      gold: "#d4af37",
      brown: "#795548",
      pink: "#e91e63",
      navy: "#001f3f",
      beige: "#f5f5dc",
    };
    const key = String(c).trim().toLowerCase();
    if (common[key]) return common[key];
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(key)) return key;
    return null;
  }, []);

  const variations = product?.variations || [];

  const availableColors = useMemo(() => {
    return variations
      .map((v) => ({
        color: v.color,
        hasAvailable: (v.sizes || []).some(isSizeAvailable),
      }))
      .filter((x) => x.color);
  }, [variations, isSizeAvailable]);

  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [quantity, setQuantity] = useState(1);

  const sizesOfColor = useMemo(() => {
    const found = variations.find((v) => v.color === color);
    return found?.sizes || [];
  }, [variations, color]);

  const selectedSize = useMemo(
    () => sizesOfColor.find((s) => s.size === size) || null,
    [sizesOfColor, size]
  );

  // Giá: ưu tiên size.price -> product.finalPrice -> product.price
  const price = useMemo(() => {
    const p1 = selectedSize?.price;
    const p2 = product?.finalPrice;
    const p3 = product?.price;
    return Number(p1 ?? p2 ?? p3 ?? 0);
  }, [selectedSize, product]);

  const stock = useMemo(() => Number(selectedSize?.stock ?? 0), [selectedSize]);

  // Default selection when open
  useEffect(() => {
    if (!open || !product) return;

    const firstColor =
      availableColors.find((c) => c.hasAvailable)?.color ||
      availableColors?.[0]?.color ||
      "";

    setColor(firstColor);

    const sizes =
      variations.find((v) => v.color === firstColor)?.sizes || [];

    const firstSize =
      sizes.find(isSizeAvailable)?.size || sizes?.[0]?.size || "";

    setSize(firstSize);
    setQuantity(1);
  }, [open, product, availableColors, variations, isSizeAvailable]);

  // Clamp quantity
  useEffect(() => {
    if (!open) return;
    if (stock <= 0) {
      setQuantity(1);
      return;
    }
    setQuantity((q) => Math.max(1, Math.min(q, stock)));
  }, [stock, open]);

  const handleChangeColor = useCallback(
    (_e, v) => {
      if (!v) return;
      setColor(v);

      const sizes = variations.find((vv) => vv.color === v)?.sizes || [];
      const first = sizes.find(isSizeAvailable)?.size || sizes?.[0]?.size || "";
      setSize(first);
      setQuantity(1);
    },
    [variations, isSizeAvailable]
  );

  const handleChangeSize = useCallback((_e, v) => {
    if (!v) return;
    setSize(v);
    setQuantity(1);
  }, []);

  const inc = useCallback(() => {
    setQuantity((q) => {
      if (stock <= 0) return 1;
      return Math.min(q + 1, stock);
    });
  }, [stock]);

  const dec = useCallback(() => {
    setQuantity((q) => Math.max(1, q - 1));
  }, []);

  const canConfirm = Boolean(color && size && stock > 0);

  const handleConfirm = useCallback(() => {
    if (!canConfirm) return;
    onConfirm({
      color,
      size,
      price,
      stock,
      quantity: Math.max(1, Math.min(quantity, stock)),
      variantId: selectedSize?.variantId,
    });
  }, [canConfirm, onConfirm, color, size, price, stock, quantity, selectedSize]);

  if (!product) return null;

  const title = product?.name || "Chọn biến thể";
  const sub = product?.sku ? `SKU: ${product.sku}` : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography fontWeight={900}>Chọn biến thể</Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
        {/* Product header */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Avatar
              src={product.image}
              variant="rounded"
              sx={{ width: 56, height: 56, borderRadius: 2 }}
            >
              {product.name?.[0]}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={900} noWrap>
                {title}
              </Typography>
              {sub && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {sub}
                </Typography>
              )}
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" color="text.secondary">
                Giá
              </Typography>
              <Typography fontWeight={900} sx={{ fontSize: smDown ? "1.05rem" : "1.2rem" }}>
                {formatMoney(price)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1.25 }} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Chip
              size="small"
              variant="outlined"
              color={stock > 0 ? "success" : "default"}
              label={stock > 0 ? `Còn: ${stock}` : "Hết hàng"}
            />
            <Typography variant="caption" color="text.secondary">
              Tạm tính:{" "}
              <b>{formatMoney(price * (stock > 0 ? quantity : 0))}</b>
            </Typography>
          </Box>
        </Paper>

        {/* Color */}
        <Box sx={{ mb: 2 }}>
          <Typography fontWeight={900} sx={{ mb: 1 }}>
            Màu sắc
          </Typography>

          <ToggleButtonGroup
            exclusive
            value={color}
            onChange={handleChangeColor}
            size="small"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              "& .MuiToggleButton-root": {
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 900,
                px: 1.25,
                py: 0.75,
              },
            }}
          >
            {availableColors.map((c) => {
              const sw = cssColor(c.color);
              const disabled = !c.hasAvailable;

              const btn = (
                <ToggleButton key={c.color} value={c.color} disabled={disabled}>
                  {sw ? (
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        mr: 1,
                        border: (t) => `1px solid ${t.palette.divider}`,
                        bgcolor: sw,
                      }}
                    />
                  ) : null}
                  {c.color}
                </ToggleButton>
              );

              return disabled ? (
                <Tooltip key={c.color} title="Không có size còn hàng">
                  <span>{btn}</span>
                </Tooltip>
              ) : (
                btn
              );
            })}
          </ToggleButtonGroup>
        </Box>

        {/* Size */}
        <Box sx={{ mb: 2 }}>
          <Typography fontWeight={900} sx={{ mb: 1 }}>
            Size
          </Typography>

          <ToggleButtonGroup
            exclusive
            value={size}
            onChange={handleChangeSize}
            size="small"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              "& .MuiToggleButton-root": {
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 900,
                px: 1.25,
                py: 0.75,
              },
            }}
          >
            {sizesOfColor.map((s) => {
              const disabled = !isSizeAvailable(s);
              const label = s.size;

              const btn = (
                <ToggleButton key={label} value={label} disabled={disabled}>
                  {label}
                </ToggleButton>
              );

              return disabled ? (
                <Tooltip key={label} title="Hết hàng hoặc tắt bán">
                  <span>{btn}</span>
                </Tooltip>
              ) : (
                btn
              );
            })}
          </ToggleButtonGroup>
        </Box>

        {/* Quantity */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography fontWeight={900}>Số lượng</Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              onClick={dec}
              disabled={!canConfirm || quantity <= 1}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>

            <TextField
              value={quantity}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (Number.isNaN(v)) return;
                if (stock <= 0) return;
                setQuantity(Math.max(1, Math.min(v, stock)));
              }}
              type="number"
              size="small"
              disabled={!canConfirm}
              inputProps={{
                min: 1,
                max: stock || 1,
                step: 1,
                style: { textAlign: "center", width: 72 },
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
            />

            <IconButton
              onClick={inc}
              disabled={!canConfirm || quantity >= stock}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ py: 1.25, px: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Hủy
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          sx={{ borderRadius: 2, fontWeight: 900 }}
        >
          Thêm vào giỏ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
